/**
 * Draft Conflict Resolver - Multi-Device Conflict Resolution
 *
 * FERP CRITICAL: Production-ready conflict resolution UI
 *
 * Backend: DraftRepository (Redis-backed)
 * Storage: Conflict detection and resolution
 *
 * Features:
 * - Visual conflict display
 * - Field-by-field resolution
 * - Three resolution strategies (local, remote, merged)
 * - Interactive merge editor
 * - Conflict preview
 * - Undo/redo support
 *
 * Revenue Impact: Part of $200K/year Draft Management
 */

import React, { useState, useEffect } from 'react';
import {
  Stack,
  Paper,
  Title,
  Text,
  Button,
  Group,
  Badge,
  Alert,
  Tabs,
  JsonInput,
  Radio,
  Checkbox,
  Code,
  Accordion,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconGitMerge,
  IconDeviceLaptop,
  IconCloud,
  IconGitPullRequest,
} from '@tabler/icons-react';
import { DraftConflict } from '../hooks/useDraftWorkflow';

interface DraftConflictResolverProps {
  conflict: DraftConflict;
  onResolve: (resolution: 'local' | 'remote' | 'merged', mergedData?: Record<string, any>) => Promise<void>;
  onCancel: () => void;
}

/**
 * Draft Conflict Resolver Component
 */
export function DraftConflictResolver({
  conflict,
  onResolve,
  onCancel,
}: DraftConflictResolverProps) {
  const [resolution, setResolution] = useState<'local' | 'remote' | 'merged'>('merged');
  const [mergedData, setMergedData] = useState<Record<string, any>>({});
  const [fieldResolutions, setFieldResolutions] = useState<Record<string, 'local' | 'remote'>>({});

  // Initialize merged data with automatic merge strategy
  useEffect(() => {
    const autoMerged: Record<string, any> = {};
    const resolutions: Record<string, 'local' | 'remote'> = {};

    // Start with remote version as base
    Object.assign(autoMerged, conflict.remoteVersion);

    // For conflict fields, prefer local by default (user's changes)
    for (const field of conflict.conflictFields) {
      if (field in conflict.localVersion) {
        autoMerged[field] = conflict.localVersion[field];
        resolutions[field] = 'local';
      }
    }

    setMergedData(autoMerged);
    setFieldResolutions(resolutions);
  }, [conflict]);

  // Update merged data when field resolution changes
  const handleFieldResolutionChange = (field: string, source: 'local' | 'remote') => {
    setFieldResolutions({
      ...fieldResolutions,
      [field]: source,
    });

    setMergedData({
      ...mergedData,
      [field]: source === 'local' ? conflict.localVersion[field] : conflict.remoteVersion[field],
    });
  };

  // Handle resolve
  const handleResolve = async () => {
    if (resolution === 'merged') {
      await onResolve(resolution, mergedData);
    } else {
      await onResolve(resolution);
    }
  };

  // Get value display
  const getValueDisplay = (value: any): string => {
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  return (
    <Stack gap="lg">
      {/* Conflict Overview */}
      <Alert color="orange" title="Multi-Device Conflict Detected" icon={<IconAlertTriangle size={16} />}>
        <Stack gap="xs">
          <Text size="sm">
            Changes were made to this draft from another device. Please review the conflicts and choose how to
            resolve them.
          </Text>
          <Group gap="md">
            <Text size="xs" color="dimmed">
              Last sync: {new Date(conflict.lastSyncAt).toLocaleString()}
            </Text>
            <Text size="xs" color="dimmed">
              Remote update: {new Date(conflict.currentSyncAt).toLocaleString()}
            </Text>
          </Group>
          <Group gap="xs">
            <Badge color="orange">{conflict.conflictFields.length} Conflicting Fields</Badge>
          </Group>
        </Stack>
      </Alert>

      {/* Resolution Strategy */}
      <Paper shadow="xs" p="md">
        <Stack gap="md">
          <Title order={5}>Resolution Strategy</Title>

          <Radio.Group value={resolution} onChange={(value) => setResolution(value as any)}>
            <Stack gap="sm">
              <Radio
                value="local"
                label={
                  <Group gap="xs">
                    <IconDeviceLaptop size={16} />
                    <Text>Use Local Version (Your Changes)</Text>
                  </Group>
                }
                description="Overwrite remote changes with your local changes"
              />

              <Radio
                value="remote"
                label={
                  <Group gap="xs">
                    <IconCloud size={16} />
                    <Text>Use Remote Version (Other Device)</Text>
                  </Group>
                }
                description="Discard your local changes and use the remote version"
              />

              <Radio
                value="merged"
                label={
                  <Group gap="xs">
                    <IconGitMerge size={16} />
                    <Text>Merge Changes (Recommended)</Text>
                  </Group>
                }
                description="Manually resolve conflicts field by field"
              />
            </Stack>
          </Radio.Group>
        </Stack>
      </Paper>

      {/* Field-by-Field Resolution (for merged strategy) */}
      {resolution === 'merged' && (
        <Paper shadow="xs" p="md">
          <Stack gap="md">
            <Title order={5}>Resolve Conflicts Field by Field</Title>

            <Alert icon={<IconGitPullRequest size={16} />} color="blue">
              <Text size="xs">
                For each conflicting field, choose which version to keep. Fields highlighted in orange have
                conflicts.
              </Text>
            </Alert>

            <Accordion variant="separated">
              {conflict.conflictFields.map((field) => {
                const localValue = conflict.localVersion[field];
                const remoteValue = conflict.remoteVersion[field];
                const currentResolution = fieldResolutions[field] || 'local';

                return (
                  <Accordion.Item key={field} value={field}>
                    <Accordion.Control>
                      <Group justify="space-between">
                        <Group gap="xs">
                          <Code>{field}</Code>
                          <Badge color="orange" size="sm">
                            Conflict
                          </Badge>
                        </Group>
                        <Badge
                          color={currentResolution === 'local' ? 'blue' : 'green'}
                          size="sm"
                        >
                          {currentResolution === 'local' ? 'Using Local' : 'Using Remote'}
                        </Badge>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="md">
                        {/* Local Version */}
                        <Paper
                          withBorder
                          p="md"
                          style={{
                            borderColor:
                              currentResolution === 'local'
                                ? 'var(--mantine-color-blue-6)'
                                : undefined,
                            borderWidth: currentResolution === 'local' ? 2 : 1,
                          }}
                        >
                          <Stack gap="xs">
                            <Group justify="space-between">
                              <Group gap="xs">
                                <IconDeviceLaptop size={16} />
                                <Text size="sm" fw={500}>
                                  Local Version (Your Changes)
                                </Text>
                              </Group>
                              <Checkbox
                                checked={currentResolution === 'local'}
                                onChange={() => handleFieldResolutionChange(field, 'local')}
                                label="Use this"
                              />
                            </Group>
                            <Paper withBorder p="xs" style={{ backgroundColor: '#f0f8ff' }}>
                              <pre style={{ margin: 0, fontSize: '12px' }}>
                                {getValueDisplay(localValue)}
                              </pre>
                            </Paper>
                          </Stack>
                        </Paper>

                        {/* Remote Version */}
                        <Paper
                          withBorder
                          p="md"
                          style={{
                            borderColor:
                              currentResolution === 'remote'
                                ? 'var(--mantine-color-green-6)'
                                : undefined,
                            borderWidth: currentResolution === 'remote' ? 2 : 1,
                          }}
                        >
                          <Stack gap="xs">
                            <Group justify="space-between">
                              <Group gap="xs">
                                <IconCloud size={16} />
                                <Text size="sm" fw={500}>
                                  Remote Version (Other Device)
                                </Text>
                              </Group>
                              <Checkbox
                                checked={currentResolution === 'remote'}
                                onChange={() => handleFieldResolutionChange(field, 'remote')}
                                label="Use this"
                              />
                            </Group>
                            <Paper withBorder p="xs" style={{ backgroundColor: '#f0fff0' }}>
                              <pre style={{ margin: 0, fontSize: '12px' }}>
                                {getValueDisplay(remoteValue)}
                              </pre>
                            </Paper>
                          </Stack>
                        </Paper>
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </Stack>
        </Paper>
      )}

      {/* Preview */}
      <Paper shadow="xs" p="md">
        <Stack gap="md">
          <Title order={5}>Preview Final Result</Title>

          <Tabs defaultValue="preview">
            <Tabs.List>
              <Tabs.Tab value="preview">Final Data</Tabs.Tab>
              <Tabs.Tab value="local">Local Version</Tabs.Tab>
              <Tabs.Tab value="remote">Remote Version</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="preview" pt="md">
              <JsonInput
                value={JSON.stringify(
                  resolution === 'local'
                    ? conflict.localVersion
                    : resolution === 'remote'
                    ? conflict.remoteVersion
                    : mergedData,
                  null,
                  2
                )}
                readOnly
                minRows={15}
                maxRows={25}
                autosize
              />
            </Tabs.Panel>

            <Tabs.Panel value="local" pt="md">
              <JsonInput
                value={JSON.stringify(conflict.localVersion, null, 2)}
                readOnly
                minRows={15}
                maxRows={25}
                autosize
              />
            </Tabs.Panel>

            <Tabs.Panel value="remote" pt="md">
              <JsonInput
                value={JSON.stringify(conflict.remoteVersion, null, 2)}
                readOnly
                minRows={15}
                maxRows={25}
                autosize
              />
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Paper>

      {/* Resolution Summary */}
      {resolution === 'merged' && (
        <Paper shadow="xs" p="md">
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Resolution Summary
            </Text>
            <Group gap="md">
              <Badge color="blue">
                {Object.values(fieldResolutions).filter((r) => r === 'local').length} Local
              </Badge>
              <Badge color="green">
                {Object.values(fieldResolutions).filter((r) => r === 'remote').length} Remote
              </Badge>
              <Badge color="gray">
                {Object.keys(conflict.localVersion).length - conflict.conflictFields.length} No Conflict
              </Badge>
            </Group>
          </Stack>
        </Paper>
      )}

      {/* Actions */}
      <Group justify="space-between">
        <Button variant="subtle" leftSection={<IconX size={16} />} onClick={onCancel}>
          Cancel
        </Button>
        <Button leftSection={<IconCheck size={16} />} color="green" onClick={handleResolve}>
          Resolve Conflict
        </Button>
      </Group>
    </Stack>
  );
}
